import React from 'react';
import { useTranslation }  from 'react-i18next';
import { RootState, useAppSelector } from '../../redux';
import { Quote } from '../../models';
import type { QuoteItem } from '../../models';
import { formatPrice } from '../../locales';
import {
    QUOTE_BILLING_PERIODS,
    quoteHasRecurringFees,
    quoteItemBillingPeriod,
    quoteItemIsRecurring,
    quoteItemLineAmount,
    quoteItemPriceSuffix,
    quotePeriodTotal,
} from '../../models/Quote';
import type { QuoteBillingPeriod } from '../../models/Quote';
import logo from '../../assets/images/logo_netoxygen.png';
import moment from 'moment';

interface QuoteTextProps {
    quote: Quote;
    part: string;
}

/** Fallback HTML (legacy quotes without snapshot). Keep in sync with yoda-server/helpers/quote_text.js */
export const DEFAULT_QUOTE_INTRO_HTML = [
    '<p>Madame, Monsieur,</p>',
    "<p>Suite à nos dernières discussions pour lesquelles nous vous remercions, nous avons le plaisir de vous faire parvenir l'offre ci-dessous sous forme d'une liste de propositions.</p>",
    "<p>Net Oxygen est une société constituée principalement d'ingénieurs issus de l'EPFL et d'autres hautes écoles suisses. Depuis maintenant plus de 20 ans, nous mettons à disposition de nos clients notre expérience en matière de services liés aux télécommunications et à Internet ainsi qu'en gestion de parcs informatiques.</p>",
    "<p>Nos clients apprécient particulièrement le fait d'avoir un interlocuteur unique et de qualité en ce qui concerne leurs moyens informatiques et de télécommunication. Nous avons également à cœur d'offrir des solutions basées sur des standards ouverts et de documenter les parcs informatiques de nos clients afin qu'ils choisissent de poursuivre leur collaboration avec nous, non pas parce que les technologies choisies les y obligent, mais parce qu'ils sont entièrement satisfaits de nos services.</p>",
    "<p>Nous vous souhaitons une bonne réception de cette offre et n'hésitez pas à prendre contact avec nous pour toute question ou demande de modification.</p>",
].join('\n');

export const DEFAULT_QUOTE_CONDITIONS_HTML = [
    "<p>Validité de l'offre : un mois</p>",
    "<p>L'offre est soumise à l'acceptation des conditions générales de Net Oxygen Sàrl, disponibles à l'adresse <a href=\"https://netoxygen.ch/conditions-generales\">https://netoxygen.ch/conditions-generales</a>. Tous les prix sont indiqués en francs suisses, hors taxes (TVA 8.1%).</p>",
].join('\n');

export function resolveQuoteIntroHtml(quote?: { introBody?: string } | null): string {
    const body = quote?.introBody?.trim();
    return body || DEFAULT_QUOTE_INTRO_HTML;
}

export function resolveQuoteConditionsHtml(quote?: { conditionsBody?: string } | null): string {
    const body = quote?.conditionsBody?.trim();
    return body || DEFAULT_QUOTE_CONDITIONS_HTML;
}

const PERIOD_TITLE_KEY: Record<QuoteBillingPeriod, string> = {
    oneTime: 'quotes.one_time_fees',
    monthly: 'quotes.monthly_fees',
    quarterly: 'quotes.quarterly_fees',
    annual: 'quotes.annual_fees',
};

function PricingTable(props: {
    items: QuoteItem[];
    total: number;
    showSuffix?: boolean;
}) {
    const { t } = useTranslation();
    const { items, total, showSuffix } = props;

    return (
        <table className="w-full">
            <thead>
                <tr>
                    <th className="p-2 bg-gray-100 dark:bg-gray-800 text-sm text-bold text-left border border-gray-200 dark:border-gray-600">{t('quotes.label')}</th>
                    <th className="p-2 bg-gray-100 dark:bg-gray-800 text-sm text-bold text-right border border-gray-200 dark:border-gray-600 border-l-none">{t('quotes.ht_price')}</th>
                </tr>
            </thead>
            <tbody>
                {items.filter((item: QuoteItem) => item.price !== 0 || item.free).map((item: QuoteItem, i: number) => (
                    <tr key={item._id || i}>
                        <td className="p-2 text-sm text-medium text-left border border-gray-200 dark:border-gray-600 flex justify-between">
                            <div dangerouslySetInnerHTML={{ __html: item.title || ''}}></div>
                            {item.free && (
                                <span className="font-bold px-2">{t('quotes.free_item')}</span>
                            )}
                        </td>
                        <td className={`p-2 text-sm text-medium text-right border border-gray-200 dark:border-gray-600 border-l-none ${item.free ? 'line-through' : ''}`}>
                            {formatPrice(quoteItemLineAmount(item) || item.price || 0)}
                            {showSuffix && !item.free ? quoteItemPriceSuffix(item) : ''}
                        </td>
                    </tr>
                ))}
            </tbody>
            <tfoot>
                <tr>
                    <td className="p-2 text-md text-medium text-right font-bold border border-gray-200 dark:border-gray-600">
                        {t('quotes.total')}
                    </td>
                    <td className="p-2 text-md text-medium text-right font-bold border border-gray-200 dark:border-gray-600 border-l-none w-36">
                        {formatPrice(total)}
                    </td>
                </tr>
            </tfoot>
        </table>
    );
}

function QuoteText(props: QuoteTextProps) {
    const { t }           = useTranslation();
    const { quote, part } = props;

    const { user } = useAppSelector((state: RootState) => state.auth);

    if (part === 'netoxygen')
        return (
            <div className="text-black text-xs flex flex-col gap-y-0 text-justify font-netox">
                <img className="h-6 w-fit mb-1" src={logo} alt="Yoda" />
                <p>Avenue d'Aïre 56 - 1203 Genève</p>
                <p>Téléphone : +41 22 364 80 00</p>
                <p>Email : info@netoxygen.ch</p>
            </div>
        );

    if (part === 'date')
        return (
            <div className="text-black text-sm">
                <p>Genève, le {moment().format('LL')}</p>
            </div>
        );

    if (part === 'intro')
        return (
            <div className="text-black text-md flex flex-col gap-y-2 text-justify">
                <div
                    className="flex flex-col gap-y-2 [&>p]:m-0"
                    dangerouslySetInnerHTML={{ __html: resolveQuoteIntroHtml(quote) }}
                />
                <p className="text-right mr-12 mt-4">
                    {user?.firstname} {user?.lastname}
                </p>
            </div>
        );

    if (part === 'approve')
        return (
            <div className="text-black text-sm flex flex-col gap-y-2 text-justify font-netox">
                <div
                    className="flex flex-col gap-y-2 [&>p]:m-0"
                    dangerouslySetInnerHTML={{ __html: resolveQuoteConditionsHtml(quote) }}
                />
                <p className="font-bold text-lg">Pour accord</p>
                <p>Société............................................................................................................................................</p>
                <p>Monsieur, Madame.......................................................................................................................</p>
                <p>Lieu, date.........................................................................................................................................</p>
                <p>Signature.........................................................................................................................................</p>
            </div>
        );

    if (part === 'pricings' && quote) {
        const hasRecurring = quoteHasRecurringFees(quote.items);

        if (!hasRecurring) {
            return (
                <PricingTable
                    items={quote.items}
                    total={quote.price || 0}
                />
            );
        }

        const storedTotals: Record<QuoteBillingPeriod, number> = {
            oneTime: quote.price || 0,
            monthly: quote.monthlyPrice || 0,
            quarterly: quote.quarterlyPrice || 0,
            annual: quote.annualPrice || 0,
        };

        return (
            <div className="flex flex-col gap-4">
                {QUOTE_BILLING_PERIODS.map((period) => {
                    const items = (quote.items || []).filter(
                        (item) => quoteItemBillingPeriod(item) === period
                    );
                    if (!items.some((item) => item.price !== 0 || item.free))
                        return null;
                    return (
                        <div key={period}>
                            <h4 className="text-base font-bold mb-2">{t(PERIOD_TITLE_KEY[period])}</h4>
                            <PricingTable
                                items={items}
                                total={storedTotals[period] || quotePeriodTotal(items, period)}
                                showSuffix={quoteItemIsRecurring({ billingPeriod: period } as QuoteItem)}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }

    return null;
}

export default QuoteText;
